import type { DataTable } from "building-types/dataTables/dataTable";
import type { Doc } from "building-types/doc";
import { docEntityBaseNew } from "building-types/entities/docEntityBaseNew";
import type { DataSequenceHandle } from "building-types/handles/docEntityHandleTypes";
import { isParameterRef } from "building-types/options/isParameterRef";
import type { Table } from "common/data/table";
import type { AtLeast } from "common/utils/atLeast";
import { snakeCaseToTitleCase } from "common/utils/strings";
import type { CustomDataSequence, DataSequence, SystemDataSequence } from "./dataSequence";
import { isSystemDataSequenceId } from "./isSystemDataSequenceId";
import type { SystemDataSequenceId } from "./systemDataSequenceTypes";
import { SystemDataSequenceIds } from "./systemDataSequenceTypes";

export function dataTableToDataSequenceHandle(dataTable: DataTable): DataSequenceHandle | undefined {
  if (isParameterRef(dataTable.dataSequenceId)) {
    return { type: "DATA_SEQUENCE", id: dataTable.dataSequenceId.id };
  } else {
    return { type: "DATA_SEQUENCE", id: dataTable.dataSequenceId };
  }
}

export function getSystemDataSequenceName(dataSequenceId: SystemDataSequenceId): string {
  return snakeCaseToTitleCase(dataSequenceId);
}

export function getDataSequenceName(
  { dataSequences }: Pick<Doc, "dataSequences">,
  { dataSequenceId }: Pick<DataTable, "dataSequenceId">,
): string | undefined {
  if (isParameterRef(dataSequenceId)) {
    return "(Parameterized)";
  }

  if (isSystemDataSequenceId(dataSequenceId)) {
    return getSystemDataSequenceName(dataSequenceId);
  }

  if (dataSequences[dataSequenceId]) {
    return dataSequences[dataSequenceId].name;
  }

  return undefined;
}

export function allDataSequenceIds(dataSequenceTable: Table<DataSequence>) {
  const sequenceIds = [...Object.keys(dataSequenceTable), ...SystemDataSequenceIds];

  const res = sequenceIds.sort((a, b) => {
    const seqA = dataSequenceTable[a]?.name ?? a;
    const seqB = dataSequenceTable[b]?.name ?? b;

    return seqA.localeCompare(seqB);
  });

  return res;
}

export function customDataSequenceNew(props: AtLeast<CustomDataSequence, "name">): CustomDataSequence {
  return {
    ...docEntityBaseNew("DATA_SEQUENCE"),
    sequenceType: "CUSTOM",
    rows: [],
    cols: [],
    ...props,
  };
}

export function systemDataSequenceNew(source: SystemDataSequenceId): SystemDataSequence {
  return {
    ...docEntityBaseNew("DATA_SEQUENCE"),
    sequenceType: "SYSTEM",
    source,
    name: getSystemDataSequenceName(source),
    id: source,
  };
}
